import { ImprintMethodModel } from '../model'
import { type LocationGroup, type PriceInfo } from '../types'
import { getTotalQty } from './use-quantity'
import { useWindow } from './use-window'
import { useProductSpec } from '../store/modules/productSpec'

export type ProductionTimeItem = {
  name: string
  title: string
  enabled: boolean
  needCalcDelivery: boolean
  selected: boolean
  rushDay: number
  priceList: PriceInfo[]
  productionTime: number
  priceDifference: number
  subtotal: number
}

export const getAllProductionTime = (isBlank: boolean) => {
  const { priceList, locationGroupList, blankProductionTime } = useWindow()


  const _priceList = priceList.filter((it: any) => {
    if (isBlank && it.BlankLimitation === 1) {
      return false
    }
    if (!isBlank && it.BlankLimitation === 2) {
      return false
    }
    return true
  })
  // 先按照rush day排序
  // 避免出现rush day和imprint.ProductionTime相同时,显示X DAY SERVICE
  _priceList.sort(function (x: any, y: any) {
    if (x.RushDay > y.RushDay) {
      return 1
    }
    if (x.RushDay === y.RushDay) {
      return 0
    }
    return -1
  })

  if (isBlank) {
    const price = _priceList[0]
    const _blankProductionTime = blankProductionTime

    return [
      {
        productionTime: _blankProductionTime,
        enabled: true,
        selected: false,
        name: price.PriceName,
        priceList: [price],
        needCalcDelivery: true,
        title: _blankProductionTime + '-Day ' + 'Service',
        rushDay: price.RushDay,
        priceDifference: 0
      } as ProductionTimeItem
    ]
  }

  const productionTimes: Record<number, ProductionTimeItem> = {}
  const productSpecState = useProductSpec()
  var totalQty = getTotalQty(productSpecState.getSelectedList)
  _priceList.forEach((price: PriceInfo) => {
    locationGroupList.forEach((imprintLocation: LocationGroup) => {
      imprintLocation.Methods.forEach((imprint) => {
        if (
          price.ImprintMethodLimitation.length === 0 ||
          price.ImprintMethodLimitation.includes(imprint.Method)
        ) {
          let productionTime = price.RushDay === 999 ? (imprint.ProductionTimeRange != null && imprint.ProductionTimeRange !== "" ? getProductionTimeByQtyRange(imprint.ProductionTimeRange, totalQty, price.Price, price.IsLTM === 1) : imprint.ProductionTime) : price.RushDay
          if (productionTimes[productionTime]) {
            productionTimes[productionTime].priceList.push(price)
          } else {
            productionTimes[productionTime] = {
              productionTime: productionTime,
              enabled: true,
              selected: false,
              name: price.PriceName,
              title: price.PriceName,
              rushDay: price.RushDay,
              needCalcDelivery: true,
              priceList: [price],
              priceDifference: 0
            } as ProductionTimeItem
          }
        }
      })
    })
  })

  const rdata = Object.values(productionTimes)
  rdata.sort((x, y) => (x.productionTime < y.productionTime ? 1 : -1))

  rdata.forEach((it) => {
    if (it.rushDay < 999) {
      it.title = `${it.rushDay}-Day Rush`
    } else {
      it.title = `${it.productionTime}-Day Service`
    }
  })
  return rdata
}

export function getProductionTimeByQtyRange(
  productionTimeRange: string,
  currentQty: number,
  price: object,
  isLTM: boolean = false
): number {
  let matchedValue = 0
  const arr1 = productionTimeRange.split(',')

  if (arr1[0].includes(":")) {
    // Convert to key value structure, then compare key with currentQty to determine which value to take
    const keyValuePairs = arr1.map(pair => {
      const parts = pair.split(':').map(item => item.trim())
      return {
        key: parseInt(parts[0], 10),
        value: parseInt(parts[1], 10)
      }
    })

    matchedValue = keyValuePairs[0].value

    // Find the largest key <= currentQty
    for (const pair of keyValuePairs) {
      if (pair.key <= currentQty) {
        matchedValue = pair.value
      } else {
        break // Since keys are in order, no need to continue
      }
    }
  } else {
    const priceQty = Object.entries(price)
      .map(([key, value]) => ({
        key: parseInt(key, 10),
        value: value
      }))

    matchedValue = parseInt(arr1[0], 10) // Default to first value

    const hasLTM = isLTM
    for (let i = 0; i < priceQty.length; i++) {
      if (priceQty[i].key <= currentQty) {
        const arrIndex = hasLTM ? Math.max(0, i - 1) : Math.min(i, arr1.length - 1)
        matchedValue = parseInt(arr1[arrIndex], 10)
      } else {
        break // Since sorted, no need to check larger keys
      }
    }
  }

  return matchedValue
}

export const getValidProductionTime = (
  priceList: PriceInfo[],
  isBlank: boolean,
  blankProductionTime: number,
  imprintMethodProductionTime: number
): Record<number, PriceInfo[]> => {
  const productionDays: Record<number, PriceInfo[]> = {}
  if (isBlank) {
    productionDays[blankProductionTime] = priceList
  } else {
    priceList.forEach((price) => {
      let productionDay = 0

      if (price.RushDay > 0 && price.RushDay < 999) {
        productionDay = price.RushDay
      } else if (price.ProductionTime === 0) {
        productionDay = imprintMethodProductionTime
      } else {
        productionDay = price.ProductionTime
      }

      if (productionDays[productionDay]) {
        productionDays[productionDay].push(price)
      } else {
        productionDays[productionDay] = [price]
      }
    })
  }
  return productionDays
}

/**
 * 获取imprint production time
 * @param selectedImprintMethodList
 * @param locationGroupList
 * @returns
 */
export const getImprintMethodProductionTime = (
  selectedImprintMethodList: ImprintMethodModel[],
  locationGroupList: LocationGroup[],
  totalQty: number,
  priceList: PriceInfo[]
) => {
  const imprintProductionTimes: number[] = []
  if (selectedImprintMethodList.length > 0) {
    selectedImprintMethodList.forEach((f) => {
      locationGroupList.some((imprintLocation) => {
        if (imprintLocation.Name === f.decorationMethod.Location) {
          imprintLocation.Methods.some((m) => {
            if (m.Method === f.decorationMethod.Method) {
              imprintProductionTimes.push(m.ProductionTimeRange != null && m.ProductionTimeRange !== "" ? getProductionTimeByQtyRange(m.ProductionTimeRange, totalQty, priceList[0].Price, priceList[0].IsLTM === 1):m.ProductionTime)
            }
          })
          return true
        }
        return false
      })
    })
  } else {
    // 取支持的imprint
    locationGroupList.forEach((imprintLocation: LocationGroup) => {
      imprintLocation.Methods.some((m) => {
        imprintProductionTimes.push(m.ProductionTimeRange != null && m.ProductionTimeRange !== "" ? getProductionTimeByQtyRange(m.ProductionTimeRange, totalQty, priceList[0].Price, priceList[0].IsLTM === 1):m.ProductionTime)
      })
    })
  }
  return Math.min(...imprintProductionTimes)
}
